import { cx } from '@linaria/core';
import { memo, PointerEvent, ReactNode, SyntheticEvent, useCallback, useState } from 'react';
import ButtonInfo from '../Buttons/ButtonInfo';
import Popover, { PopoverOrigin } from '../common/Popover';
import { PAPER_HEIGHTS } from '../theme';
import "./InfoTooltip.linaria.module.css!=!./InfoTooltip?./InfoTooltip.linaria.module.css?css=H4sIAAAAAAAAA22QwW6DMAyG732KHrcDiNCVAb3sTaKMGOLWIRG4hTbqu09CYmvQjr/9fZb8p4qtNFKYwp2vjzRKQRF2fYIMdqwb6BmG03O3MNUoBc8T3ihoHD2pe90SzOu6KeVEWEATp9C6npMRH1BnaSny4wB2Vc4HKXyvsFLBqjmZULOpP/LM/x4lJ1vukPI4Ba+0xr5L2Pm6fMEhwmGLfztmZyPjEhmXrUHQcsRTxNOWH7Azq/BlQaPav/39ti+yzM/vYZHFfKtcfpRCfJZ0Nf/NXmrJy6WW5+4HwDSNlb4BAAA="
const rootStyles = "atm_h_1h6ojuz atm_9s_1txwivl";
const popoverStyles = "atm_h_1h6ojuz atm_9s_1txwivl atm_c8_wli6ec atm_j3_1pnai9a atm_lo_ftgil2 atm_le_ftgil2 atm_lk_ftgil2 atm_ll_ftgil2 atm_1xv9o25_1178luh";
interface TooltipProps {
  /** must be a single memoized component */
  children: ReactNode;
  styleOverrides?: string;
  iconStyleOverrides?: string;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  fontSize?: 'small' | 'medium' | 'large' | 'inherit';
}
interface TooltipState {
  anchorEl: HTMLElement | null;
  /** opened by a click/tap: stays up until the backdrop or Escape closes it */
  pinned: boolean;
}
const CLOSED: TooltipState = {
  anchorEl: null,
  pinned: false
};

/* a touch "hover" is the finger landing on the button right before its
   click, so only a mouse (or pen) hover previews the tooltip; touch opens it
   through the click, pinned, since there is no pointerleave to close it */
const canHover = (event: PointerEvent) => event.pointerType !== 'touch';

/**
 * The (i) button and its popover. Hovering previews the text and moving away
 * dismisses it; clicking or tapping pins it until the user clicks outside or
 * clicks the button again.
 */
const Tooltip = (props: TooltipProps) => {
  const [{
    anchorEl,
    pinned
  }, setState] = useState<TooltipState>(CLOSED);
  const handlePointerEnter = useCallback((event: PointerEvent<HTMLButtonElement>) => {
    if (!canHover(event)) return;
    const target = event.currentTarget;
    setState(state => state.anchorEl ? state : {
      anchorEl: target,
      pinned: false
    });
  }, []);
  const handlePointerLeave = useCallback((event: PointerEvent<HTMLButtonElement>) => {
    if (!canHover(event)) return;
    setState(state => state.pinned ? state : CLOSED);
  }, []);
  const handleClick = useCallback((event: SyntheticEvent) => {
    const target = event.currentTarget as HTMLElement;
    setState(state => state.pinned ? CLOSED : {
      anchorEl: target,
      pinned: true
    });
  }, []);
  const handleClose = useCallback(() => {
    setState(CLOSED);
  }, []);
  const {
    fontSize,
    children,
    styleOverrides,
    iconStyleOverrides,
    anchorOrigin,
    transformOrigin
  } = props;
  return <div className={cx(rootStyles, styleOverrides)}>
            <ButtonInfo fontSize={fontSize} onClick={handleClick} onPointerEnter={handlePointerEnter} onPointerLeave={handlePointerLeave} overrides={iconStyleOverrides} />
            <Popover anchorEl={anchorEl} anchorOrigin={anchorOrigin} className={popoverStyles} data-testid="InfoTooltip__Popover" onClose={handleClose} open={Boolean(anchorEl)} passive={!pinned} transformOrigin={transformOrigin} zIndex={PAPER_HEIGHTS.tooltip}>
                {children}
            </Popover>
        </div>;
};
export default memo(Tooltip);